import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './Header';
import Homepage from './HomePage';
import PostSplitter from '../Tools/PostSplitter/PostSplitter';
import PrivacyPolicy from './PrivacyPolicy';
import TermsConditions from './TermsConditions';
import ScrollToTop from './shared/ScrollToTop';
import ProvideFeedbackPage from "./shared/ProvideFeedbackPage";
import AboutUs from './AboutUs';
import BuyMeACoffee from './shared/BuyMeACoffee';

// Lazy load tool components
const SentimentAnalyzer = React.lazy(() => import('../Tools/Sentiment/SentimentAnalyzer'));
const HashtagsAnalyzer = React.lazy(() => import('../Tools/Hashtags/HashtagsAnalyzer'));
const EmojisAnalyzer = React.lazy(() => import('../Tools/Emojis/EmojisAnalyzer'));
const AITextAnalyzer = React.lazy(() => import('../Tools/DetectAI/AITextAnalyzer'));
const ReadabilityAnalyzer = React.lazy(() => import('../Tools/ReadabilityChecker/ReadabilityAnalyzer'));
const TextSummarizationAnalyzer = React.lazy(() => import('../Tools/Summarization/TextSummarizationAnalyzer'));
const KeywordExtractionAnalyzer = React.lazy(() => import('../Tools/KeywordExtraction/KeywordExtractionAnalyzer'));
const SemanticSimilarityAnalyzer = React.lazy(() => import('../Tools/SemanticSimilarity/SemanticSimilarityAnalyzer'));
const HumanizerAnalyzer = React.lazy(() => import('../Tools/Humanizer/HumanizerAnalyzer'));
const RepurposerAnalyzer = React.lazy(() => import('../Tools/Repurposer/RepurposerAnalyzer'));
const SimplifierAnalyzer = React.lazy(() => import('../Tools/Simplifier/SimplifierAnalyzer'));
const SEOAnalyzer = React.lazy(() => import('../Tools/SEO/SEOAnalyzer'));
const QuoteAnalyzer = React.lazy(() => import('../Tools/QuoteCard/QuoteAnalyzer'));
const FlashcardAnalyzer = React.lazy(() => import('../Tools/Flashcards/FlashcardAnalyzer'));

// Lazy load blog components
const BlogHome = React.lazy(() => import('./Blog/BlogHome'));
const BlogPost = React.lazy(() => import('./Blog/BlogPost'));

// Tool route paths where Buy Me a Coffee should appear
const toolRoutes = [
  '/PostSplitter',
  '/SentimentAnalyzer',
  '/HashtagsAnalyzer',
  '/EmojisAnalyzer',
  '/AITextAnalyzer',
  '/ReadabilityAnalyzer',
  '/TextSummarizationAnalyzer',
  '/KeywordExtractionAnalyzer',
  '/SemanticSimilarityAnalyzer',
  '/HumanizerAnalyzer',
  '/RepurposerAnalyzer',
  '/SimplifierAnalyzer',
  '/SEOAnalyzer',
  '/QuoteAnalyzer',
  '/FlashcardAnalyzer',
];

// Loading component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    Loading...
  </div>
);

// Component to conditionally render Buy Me a Coffee on tool pages
const ToolPageBuyMeACoffee = () => {
  const location = useLocation();
  const isToolPage = toolRoutes.includes(location.pathname);

  if (!isToolPage) return null;

  return <BuyMeACoffee variant="floating" />;
};

export default function Paperbase() {
  return (
    <>
      <ScrollToTop />
      <Header />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/blog" element={<BlogHome />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/PostSplitter" element={<PostSplitter />} />
          <Route path="/SentimentAnalyzer" element={<SentimentAnalyzer />} />
          <Route path="/HashtagsAnalyzer" element={<HashtagsAnalyzer />} />
          <Route path="/EmojisAnalyzer" element={<EmojisAnalyzer />} />
          <Route path="/AITextAnalyzer" element={<AITextAnalyzer />} />
          <Route path="/ReadabilityAnalyzer" element={<ReadabilityAnalyzer />} />
          <Route path="/TextSummarizationAnalyzer" element={<TextSummarizationAnalyzer />} />
          <Route path="/KeywordExtractionAnalyzer" element={<KeywordExtractionAnalyzer />} />
          <Route path="/SemanticSimilarityAnalyzer" element={<SemanticSimilarityAnalyzer />} />
          <Route path="/HumanizerAnalyzer" element={<HumanizerAnalyzer />} />
          <Route path="/RepurposerAnalyzer" element={<RepurposerAnalyzer />} />
          <Route path="/SimplifierAnalyzer" element={<SimplifierAnalyzer />} />
          <Route path="/SEOAnalyzer" element={<SEOAnalyzer />} />
          <Route path="/QuoteAnalyzer" element={<QuoteAnalyzer />} />
          <Route path="/FlashcardAnalyzer" element={<FlashcardAnalyzer />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          <Route path="/provide-feedback" element={<ProvideFeedbackPage />} />
        </Routes>
      </Suspense>

      {/* Buy Me a Coffee - Only shown on tool pages */}
      <ToolPageBuyMeACoffee />
    </>
  );
}
